export const hexToRgba = (hex: string | undefined, opacity: number = 0.1): string => {
  if (!hex) return `rgba(0, 0, 0, ${opacity})`;

  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Convertit une couleur HTML (hex, rgb, hsl, ou nom) en RGBA avec assombrissement et opacité
 * @param color - Couleur HTML (ex: "#ff0000", "rgb(255,0,0)", "red")
 * @param darkenPercent - Pourcentage d'assombrissement (0-100, défaut: 50)
 * @param opacity - Opacité finale (0-1, défaut: 0.7)
 * @returns - Couleur RGBA assombrie
 */
const darkenColorWithOpacity = (
  color: string | undefined,
  darkenPercent: number = 50,
  opacity: number = 0.7
): string => {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  // Créer un élément temporaire pour parser la couleur
  const tempDiv = document.createElement('div');
  tempDiv.style.color = color;
  document.body.appendChild(tempDiv);

  const computedColor = window.getComputedStyle(tempDiv).color;
  document.body.removeChild(tempDiv);

  // Extraire les valeurs RGB
  const rgbMatch = computedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);

  if (!rgbMatch) {
    console.warn(`Could not parse color: ${color}`);
    return `rgba(0, 0, 0, ${opacity})`;
  }

  // Convertir en nombres et assombrir
  const r = Math.round(parseInt(rgbMatch[1]) * (1 - darkenPercent / 100));
  const g = Math.round(parseInt(rgbMatch[2]) * (1 - darkenPercent / 100));
  const b = Math.round(parseInt(rgbMatch[3]) * (1 - darkenPercent / 100));

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Version optimisée qui évite la manipulation DOM pour les couleurs hex
 * @param color - Couleur HTML
 * @param darkenPercent - Pourcentage d'assombrissement (0-100)
 * @param opacity - Opacité finale (0-1)
 * @returns - Couleur RGBA assombrie
 */
export const darkenHexColorWithOpacity = (
  color: string | undefined,
  darkenPercent: number = 50,
  opacity: number = 0.7
): string => {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  // Si c'est une couleur hex, la traiter directement
  if (color.startsWith('#')) {
    const cleanHex = color.replace('#', '');

    // Vérifier si c'est un hex valide
    if (!/^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
      console.warn(`Invalid hex color: ${color}`);
      return `rgba(0, 0, 0, ${opacity})`;
    }

    // Parser et assombrir
    const r = Math.round(parseInt(cleanHex.substring(0, 2), 16) * (1 - darkenPercent / 100));
    const g = Math.round(parseInt(cleanHex.substring(2, 4), 16) * (1 - darkenPercent / 100));
    const b = Math.round(parseInt(cleanHex.substring(4, 6), 16) * (1 - darkenPercent / 100));

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Pour les autres formats, utiliser la méthode avec DOM
  return darkenColorWithOpacity(color, darkenPercent, opacity);
};
